<template>
  <div
    class="comunication-bar"
    :class="{ showingSideBar: isComunicationSidebarVisible }"
  >
    <ul class="alert__list">
      <li id="tabNotificacoes">
        <div class="tab-container">
          <tab
            :label="'INFORMATIVOS'"
            :messages-amount="getAlertsNumber"
            class="highlighted"
          />
          <button
            type="button"
            class="comunication-bar__close"
            aria-label="Fechar informativos"
            title="Fechar informativos"
            @click="setComunicationSidebarShowOff(false)"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div
          v-if="isComunicationSidebarVisible && alertsForLaboratoryLoadingStatus === 'STATUS_LOADING'"
          class="notification-status"
          role="status"
        >
          <spinner class="small" />
          <p>Carregando informativos...</p>
        </div>

        <div
          v-if="isComunicationSidebarVisible && alertsForLaboratoryLoadingStatus === 'STATUS_FAILED'"
          class="notification-status notification-status--failed"
          role="alert"
        >
          <svg-loader src="/static/images/icon-failed-red.svg" />
          <p>Não foi possível buscar os informativos. Tente novamente.</p>
          <button
            type="button"
            class="btn btn-nonenary btn-small"
            @click="refresh"
          >
            Tentar novamente
          </button>
        </div>

        <div
          v-if="isComunicationSidebarVisible && failedReadNotification"
          class="notification-status notification-status--failed notification-read-error"
          role="alert"
        >
          <svg-loader src="/static/images/icon-failed-red.svg" />
          <p>Não foi possível marcar o informativo como visualizado. Tente novamente.</p>
          <button
            type="button"
            class="btn btn-nonenary btn-small"
            @click="markAlertAsRead(failedReadNotification)"
          >
            Tentar novamente
          </button>
        </div>

        <transition-group
          id="notification_container"
          name="slides-fade"
          tag="div"
          class="alerts-container showContainer"
          enter-active-class="slid-fade-enter-active"
          leave-active-class="slides-fade-leave-active"
        >
          <div
            v-for="(message, index) in notificationsForLaboratories"
            :key="'alert-' + index"
            class="message-container"
          >
            <icon :team="message.type" />
            <message-box
              :message="message"
              :read-pending="isLaboratoryAlertReadPending(message)"
              @read-message="markAlertAsRead(message)"
              @click.native="openNotification(message)"
            />
          </div>
        </transition-group>

        <empty-message
          v-if="alertsForLaboratoryLoadingStatus === 'STATUS_SUCCESS' && notificationsForLaboratories.length === 0 && readedNotifications.length === 0"
          type="informativos"
          class="showContainer"
        />

        <div class="readed-notifications showContainer">
          <h5 id="viewed-informatives-title">Visualizados</h5>
        </div>

        <transition-group
          id="read_notification_container"
          name="slides-fade"
          tag="section"
          aria-labelledby="viewed-informatives-title"
          class="alerts-container showContainer"
          enter-active-class="slid-fade-enter-active"
          leave-active-class="slides-fade-leave-active"
        >
          <div
            v-for="(message, index) in readedNotifications"
            :key="'alert-readed' + index"
            class="message-container"
          >
            <icon :team="message.type" />
            <message-box
              :message="message"
              @click.native="openNotification(message)"
            />
          </div>
          <p
            v-if="alertsForLaboratoryLoadingStatus === 'STATUS_SUCCESS' && readedNotifications.length === 0"
            key="empty-history"
            class="readed-notifications__empty"
          >
            Nenhum informativo visualizado.
          </p>
        </transition-group>
      </li>
    </ul>

    <NotificationModal
      ref="notificationModal"
      :title="notificationTitle"
      :url="notificationLink"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Spinner from '@/components/common/Spinner'

import MessageBox from './MessageBox'
import EmptyMessage from './EmptyMessage'
import Tab from './Tab'
import Icon from './Icon'
import NotificationModal from '../NotificationModal.vue'

function getNotificationId(notification) {
  const id = notification && notification['id-notification']
  return id === undefined || id === null ? null : String(id).trim() || null
}

export default {
  name: 'ComunicationBar',
  components: {
    'message-box': MessageBox,
    'empty-message': EmptyMessage,
    tab: Tab,
    icon: Icon,
    NotificationModal,
    spinner: Spinner,
  },
  data: () => {
    return {
      notificationLink: '',
      notificationTitle: '',
      failedReadNotification: null,
    }
  },
  computed: {
    ...mapGetters([
      'isComunicationSidebarVisible',
      'getAlertsNumber',
      'notificationsForLaboratories',
      'alertsForLaboratoryLoadingStatus',
      'isLaboratoryAlertReadPending',
      'profile',
      'readedNotifications',
    ]),
  },
  watch: {
    isComunicationSidebarVisible(visible) {
      if (!visible) {
        this.failedReadNotification = null
        this.refresh()
      }
    },
    notificationsForLaboratories() {
      if (this.failedReadNotification) {
        this.failedReadNotification = this.findUnreadNotification(this.failedReadNotification)
      }
    },
  },
  methods: {
    ...mapActions([
      'getAlertsForLaboratory',
      'markLaboratoryAlertAsRead',
      'setComunicationSidebarShowOff',
    ]),
    async markAlertAsRead(notification) {
      const profile = this.profile
      if (getNotificationId(this.failedReadNotification) === getNotificationId(notification)) {
        this.failedReadNotification = null
      }
      const markedAsRead = await this.markLaboratoryAlertAsRead(notification)
      const currentNotification = this.findUnreadNotification(notification)
      if (!markedAsRead && this.isComunicationSidebarVisible && this.profile === profile && currentNotification) {
        this.failedReadNotification = currentNotification
      }
      return markedAsRead
    },
    findUnreadNotification(notification) {
      const id = getNotificationId(notification)
      if (id === null) return null
      return this.notificationsForLaboratories.find(item => getNotificationId(item) === id) || null
    },
    refresh() {
      return this.getAlertsForLaboratory()
    },
    openNotification(notification) {
      if (notification.link) {
        this.notificationTitle = notification.title
        this.notificationLink = notification.link

        this.$nextTick(() => {
          this.$refs.notificationModal.openModal()
        })
      }
      if (!notification.read) {
        this.markAlertAsRead(notification)
      }
    },
  },
}
</script>

<style lang="scss">
@import '../../../../static/stylesheets/variables';

.comunication-bar {
  background: linear-gradient(to left, #f1f1f1 90%, rgba(0, 0, 0, 0) 10%);
  width: 0;
  flex-grow: 0;
  margin-top: $mainHeaderHeight;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 104;
  transition: width 0.3s;
  transition-delay: 0.15s;
  height: calc(100vh - #{$mainHeaderHeight});
  max-width: 100vw;
  overflow: hidden;

  .notification-status {
    width: 90%;
    float: right;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: #f4f4f4;
    color: #4c4f59;
    text-align: center;

    p {
      margin: 0;
    }

    &--failed {
      color: #b40607;

      svg {
        width: 24px;
        height: 24px;
      }
    }
  }

  .readed-notifications {
    flex-wrap: wrap;
    width: 90%;
    float: right;
    z-index: 100;
    background-color: #f4f4f4;
    position: relative;

    h5 {
      padding: 15px 14px 15px 0;
      color: #fff;
      width: 100%;
      display: flex;
      justify-content: center;
      font-weight: 700;
      background: #d0d0d0;
      text-transform: uppercase;
    }

    &__empty {
      width: 90%;
      margin: 0 0 0 auto;
      padding: 20px;
      background: #f4f4f4;
      color: #4c4f59;
      text-align: center;
      font-size: 12px;
    }
  }

  .tab-container {
    position: relative;
    display: flex;
    width: 90%;
    float: right;
    background: #f4f4f4;

    .tab-label {
      width: 100%;
      padding-left: 44px;
      padding-right: 44px;
      cursor: default;

      .label {
        cursor: default;
      }
    }

    .highlighted {
      border-bottom-color: #840051;
      color: #840051;
    }
  }

  &__close {
    position: absolute;
    top: 10px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: $theme-primary-color;
    font-size: 28px;
    line-height: 1;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid $theme-primary-color;
      outline-offset: 2px;
    }
  }

  .alert {
    &__list {
      overflow-y: auto;
      overflow-x: hidden;
      height: 100%;
      -ms-overflow-style: -ms-autohiding-scrollbar;
      -ms-overflow-style: none;
      padding-right: 0;
      width: 100%;
      box-sizing: border-box;
    }

    &__list::-webkit-scrollbar {
      width: 0px;
      background: transparent; /* make scrollbar transparent */
    }
    &__item {
      display: flex;
      flex-direction: column;
      cursor: pointer;
      position: relative;
      outline: 0;
      margin: 0;

      &:not(.md-button) {
        color: #4c4f59;
      }

      &:hover {
        z-index: 10;

        &:not(.md-button) {
          color: #4c4f59;
          text-decoration: none;
        }

        .alert__item__icon {
          border-left: 3px solid #7f1a54;
        }

        .second-level {
          height: 65px;
          transition: height 0.2s;
        }
      }

      &.active {
        z-index: 10;

        .alert__item__text {
          color: #4c4f59;
        }

        .alert__item__icon {
          border-left: 3px solid #7f1a54;
        }
      }

      .second-level {
        width: 100%;
        height: 0px;
        overflow: hidden;
        transition: height 0.2s;
      }

      &__icon {
        padding: 12px 0;
        border-left: 3px solid #fff;
        height: 50px;
        width: 50px;
        display: flex;
        justify-content: center;
        margin: 0;

        svg {
          width: 22px;
        }

        &.narrow svg {
          width: 18px;
        }
      }
    }
    &__subalert {
      margin: 2px 0 24px 52px;

      &__item {
        font-size: 12px;
        color: #949497;
        text-align: left;
        margin: 0 0 13px 4px;
        font-weight: 500;
        display: flex;
        flex-wrap: nowrap;
        white-space: nowrap;

        &::before {
          content: '■';
          color: #7f1a54;
          display: inline-block;
          width: 17px;
          margin-right: 10px;
          font-size: 8px;
        }

        &:hover {
          color: #4c4f59;
        }

        &:last-child {
          margin-bottom: 0;
        }

        a,
        a:hover,
        a:visited {
          color: inherit;
          text-decoration: none;
        }
      }
    }
  }

  &.showingSideBar {
    width: 450px;
    transition: width 0.3s;
    padding-bottom: 40px;
  }

  .remove-margin {
    margin-bottom: 0 !important;
  }
}

.showContainer {
  display: flex !important;
}

.message-container {
  width: 100%;
  display: flex;
}

.alerts-container {
  display: none;
  flex-wrap: wrap;
  width: 100%;
}

.slid-fade-enter-active {
  transition: opacity 0.4s;
  transition-delay: 0.4s;
}

.slides-fade-leave-active {
  transition: all 0.4s cubic-bezier(1, 0.5, 0.8, 1);
  -webkit-transition: all 0.4s cubic-bezier(1, 0.5, 0.8, 1);
  transform: translateX(400px);
  transition-delay: 0.2s;
}
.slides-fade-enter
  /* .slides-fade-leave-active em versões anteriores a 2.1.8 */ {
  opacity: 0;
  transition-delay: 0.15s;
}

.slides-fade-leave-to {
  opacity: 0;
  transition-delay: 0.15s;
}
</style>
